import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import PeriodFilter, { calcRange } from '../components/PeriodFilter';
import PaginationFooter from '../components/PaginationFooter';

const THX_RED = '#B71530';

const STATUS_OPTIONS = ['pending', 'approved', 'on_hold', 'disputed', 'dismissed', 'escalated'];

const STATUS_BADGES = {
  pending: 'bg-warning text-dark',
  approved: 'bg-success',
  on_hold: 'bg-secondary',
  disputed: 'bg-warning',
  dismissed: 'bg-dark',
  escalated: 'bg-danger',
};

function fmt(val, prefix = '£') {
  if (val == null) return '–';
  return `${prefix}${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s) {
  if (!s) return '–';
  try {
    return new Date(s).toLocaleDateString('en-GB');
  } catch {
    return s;
  }
}


export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState('30');
  const [dateFrom, setDateFrom] = useState(() => calcRange('30').from);
  const [dateTo,   setDateTo]   = useState(() => calcRange('30').to);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [needsReview, setNeedsReview] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newNote, setNewNote] = useState('');
  const [msg, setMsg] = useState('');

  const fetchInvoices = useCallback(() => {
    setLoading(true);
    const params = { page, per_page: pageSize };
    if (statusFilter) params.status = statusFilter;
    if (supplierFilter) params.supplier = supplierFilter;
    if (needsReview) params.needs_review = true;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo)   params.date_to   = dateTo;

    client.get('/invoices/', { params })
      .then(r => {
        setInvoices(r.data.invoices || []);
        setTotal(r.data.total || 0);
        setPages(r.data.pages || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, pageSize, statusFilter, supplierFilter, needsReview, dateFrom, dateTo]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleRowClick = (inv) => {
    setSelected(inv);
    setNewStatus(inv.status || 'pending');
    setNewNote('');
    setMsg('');
  };

  const handleStatusUpdate = async () => {
    if (!selected) return;
    setUpdatingStatus(true);
    try {
      await client.patch(`/invoices/${selected.id}/status`, { status: newStatus, note: newNote });
      setMsg('Status updated');
      setSelected(prev => ({ ...prev, status: newStatus }));
      fetchInvoices();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Update failed');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  return (
    <div>
      {/* Period filter */}
      <PeriodFilter
        preset={preset} dateFrom={dateFrom} dateTo={dateTo}
        onPresetChange={d => { const r = calcRange(d); setPreset(d); setDateFrom(r.from); setDateTo(r.to); setPage(1); }}
        onFromChange={e => { setDateFrom(e.target.value); setPreset('custom'); setPage(1); }}
        onToChange={e => { setDateTo(e.target.value); setPreset('custom'); setPage(1); }}
      />

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <form onSubmit={handleSearch} className="row g-2 align-items-end">
            <div className="col-auto">
              <label className="form-label mb-1" style={{ fontSize: '12px' }}>Status</label>
              <select
                className="form-select form-select-sm"
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
            <div className="col">
              <label className="form-label mb-1" style={{ fontSize: '12px' }}>Supplier</label>
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search supplier..."
                value={supplierFilter}
                onChange={e => setSupplierFilter(e.target.value)}
              />
            </div>
            <div className="col-auto">
              <div className="form-check mb-1">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="needsReview"
                  checked={needsReview}
                  onChange={e => { setNeedsReview(e.target.checked); setPage(1); }}
                />
                <label className="form-check-label" htmlFor="needsReview" style={{ fontSize: '13px' }}>
                  Needs Review
                </label>
              </div>
            </div>
            <div className="col-auto">
              <button type="submit" className="btn btn-sm" style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}>
                <i className="bi bi-search" />
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="row g-3">
        {/* Table */}
        <div className={selected ? 'col-md-7' : 'col-12'}>
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center py-2">
              <span style={{ fontSize: '14px', fontWeight: '600' }}>
                Invoices ({total})
              </span>
            </div>
            <div className="card-body p-0">
              {loading ? (
                <div className="text-center py-5"><div className="spinner-border text-danger spinner-border-sm" /></div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-sm mb-0" style={{ fontSize: '13px' }}>
                    <thead className="table-light">
                      <tr>
                        <th>Supplier</th>
                        <th>Invoice #</th>
                        <th>Date</th>
                        <th className="text-end">Amount</th>
                        <th>Currency</th>
                        <th>Status</th>
                        <th>Review</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.length === 0 && (
                        <tr><td colSpan="7" className="text-center text-muted py-4">No invoices found</td></tr>
                      )}
                      {invoices.map(inv => (
                        <tr
                          key={inv.id}
                          onClick={() => handleRowClick(inv)}
                          style={{ cursor: 'pointer', backgroundColor: selected?.id === inv.id ? '#fff3cd' : undefined }}
                        >
                          <td style={{ maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {inv.matched_supplier_name || inv.supplier_name || '–'}
                          </td>
                          <td>{inv.invoice_number || '–'}</td>
                          <td>{fmtDate(inv.invoice_date)}</td>
                          <td className="text-end">{fmt(inv.gross_amount, inv.currency === 'GBP' ? '£' : inv.currency + ' ')}</td>
                          <td>{inv.currency || 'GBP'}</td>
                          <td>
                            <span className={`badge ${STATUS_BADGES[inv.status] || 'bg-secondary'}`}>
                              {inv.status?.replace(/_/g, ' ') || 'pending'}
                            </span>
                          </td>
                          <td>
                            {inv.needs_review && !inv.reviewed && (
                              <span className="badge bg-warning text-dark">Review</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {!loading && total > 0 && (
              <PaginationFooter
                page={page}
                totalPages={pages}
                total={total}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>

        {/* Detail panel */}
        {selected && (
          <div className="col-md-5">
            <div className="card" style={{ position: 'sticky', top: '20px' }}>
              <div className="card-header d-flex justify-content-between align-items-center py-2">
                <span style={{ fontSize: '14px', fontWeight: '600' }}>Invoice Detail</span>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelected(null)}>
                  <i className="bi bi-x" />
                </button>
              </div>
              <div className="card-body" style={{ fontSize: '13px' }}>
                <dl className="row mb-2">
                  <dt className="col-5">Supplier</dt>
                  <dd className="col-7">{selected.matched_supplier_name || selected.supplier_name || '–'}</dd>
                  <dt className="col-5">Invoice #</dt>
                  <dd className="col-7">{selected.invoice_number || '–'}</dd>
                  <dt className="col-5">Invoice Date</dt>
                  <dd className="col-7">{fmtDate(selected.invoice_date)}</dd>
                  <dt className="col-5">Due Date</dt>
                  <dd className="col-7">{fmtDate(selected.due_date)}</dd>
                  <dt className="col-5">PO Number</dt>
                  <dd className="col-7">{selected.po_number || '–'}</dd>
                  <dt className="col-5">Net</dt>
                  <dd className="col-7">{fmt(selected.net_amount)}</dd>
                  <dt className="col-5">Tax</dt>
                  <dd className="col-7">{fmt(selected.tax_amount)}</dd>
                  <dt className="col-5">Gross</dt>
                  <dd className="col-7"><strong>{fmt(selected.gross_amount)}</strong></dd>
                  <dt className="col-5">Currency</dt>
                  <dd className="col-7">{selected.currency || 'GBP'}</dd>
                  <dt className="col-5">Mailbox</dt>
                  <dd className="col-7">{selected.mailbox || '–'}</dd>
                  <dt className="col-5">Sender</dt>
                  <dd className="col-7" style={{ wordBreak: 'break-all' }}>{selected.sender || '–'}</dd>
                  <dt className="col-5">Confidence</dt>
                  <dd className="col-7">{selected.extraction_confidence != null ? `${(selected.extraction_confidence * 100).toFixed(0)}%` : '–'}</dd>
                  <dt className="col-5">Review reason</dt>
                  <dd className="col-7">{selected.review_reason || '–'}</dd>
                </dl>

                {selected.file_path && (
                  <a
                    href={`/api/invoices/${selected.id}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary w-100 mb-3"
                  >
                    <i className="bi bi-file-earmark-pdf me-1" /> View PDF
                  </a>
                )}

                <div className="border-top pt-3">
                  <div className="mb-2">
                    <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: '600' }}>Update Status</label>
                    <select
                      className="form-select form-select-sm mb-2"
                      value={newStatus}
                      onChange={e => setNewStatus(e.target.value)}
                    >
                      {STATUS_OPTIONS.map(s => (
                        <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                      ))}
                    </select>
                    <input
                      type="text"
                      className="form-control form-control-sm mb-2"
                      placeholder="Note (optional)"
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                    />
                    <button
                      className="btn btn-sm w-100"
                      style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
                      onClick={handleStatusUpdate}
                      disabled={updatingStatus}
                    >
                      {updatingStatus ? 'Updating...' : 'Update Status'}
                    </button>
                    {msg && <div className="alert alert-info py-1 mt-2 mb-0" style={{ fontSize: '12px' }}>{msg}</div>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
